import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";
import "./TravelersPopUp.scss";
import Button from "../../ui/Button/Button";
import { commonDivisors } from "../../../utils";
import { IoClose } from "react-icons/io5";

const TravelersPopUp = ({ isOpen, onClose, onSelect, selectedValue }) => {
  const intl = useIntl();
  const modalRef = useRef(null);

  const [adults, setAdults] = useState(selectedValue?.value?.adults || 1);
  const [children, setChildren] = useState(selectedValue?.value?.children || 0);
  const [rooms, setRooms] = useState(selectedValue?.value?.rooms || 1);
  const [childrenAges, setChildrenAges] = useState(
    selectedValue?.value?.childrenAges || []
  );
  const [multiRooms, setMultiRooms] = useState([]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };

    // Delay adding the listener to avoid catching the same mousedown event that opened the popup
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setMultiRooms(
      commonDivisors(adults * 1, children * 1 === 0 ? adults * 1 : children * 1)
    );

    const maxRooms = adults + children;
    if (rooms > maxRooms) {
      setRooms(maxRooms);
    }
  }, [adults, children]);

  useEffect(() => {
    let newAges = [...childrenAges];

    if (newAges.length > children) {
      newAges = newAges.slice(0, children);
    }

    while (newAges.length < children) {
      newAges.push(0);
    }

    setChildrenAges(newAges);
  }, [children]);

  useEffect(() => {
    if (selectedValue?.value) {
      setAdults(selectedValue.value.adults || 1);
      setChildren(selectedValue.value.children || 0);
      setRooms(selectedValue.value.rooms || 1);
      setChildrenAges(selectedValue.value.childrenAges || 0);
    }
  }, [selectedValue]);

  const increment = (setter, value) => {
    setter(value + 1);
  };

  const decrement = (setter, value) => {
    if (value > 0) {
      setter(value - 1);
    }
  };

  const handleApply = () => {
    const totalGuests = adults + children;

    const roomText =
      rooms === 1
        ? intl.formatMessage({ id: "roomSingular" })
        : intl.formatMessage({ id: "roomsPlural" });

    const guestText =
      totalGuests === 1
        ? intl.formatMessage({ id: "personSingular" })
        : intl.formatMessage({ id: "personsPlural" });

    const label = `${totalGuests} ${guestText}, ${rooms} ${roomText}`;

    onSelect({
      label,
      value: { adults, children, rooms, childrenAges },
    });

    handleClose();
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="travelers-popup-overlay">
      <div className="travelers-popup-modal" ref={modalRef}>
        <div className="popup-header">
          <div className="popup-header-text">
            <span className="popup-title">
              {intl.formatMessage({ id: "travelersAndRooms" })}
            </span>
            {/* <span className="popup-subtitle">
              {intl.formatMessage({ id: "personsAndRooms" })}
            </span> */}
          </div>
          <button className="popup-close-button" onClick={handleClose}>
            <IoClose />
          </button>
        </div>

        <div className="popup-content">
          <div className="travelers-section">
            <div className="traveler-row">
              <span className="traveler-label">
                {intl.formatMessage({ id: "adults" })}
              </span>
              <div className="counter">
                <button
                  className="counter-btn"
                  onClick={() => decrement(setAdults, adults)}
                  disabled={adults <= 1}
                >
                  -
                </button>
                <span className="counter-value">{adults}</span>
                <button
                  className="counter-btn"
                  onClick={() => increment(setAdults, adults)}
                  disabled={adults >= 6}
                >
                  +
                </button>
              </div>
            </div>

            <div className="traveler-row">
              <span className="traveler-label">
                {intl.formatMessage({ id: "children" })}
              </span>
              <div className="counter">
                <button
                  className="counter-btn"
                  onClick={() => decrement(setChildren, children)}
                  disabled={children <= 0}
                >
                  -
                </button>
                <span className="counter-value">{children}</span>
                <button
                  className="counter-btn"
                  onClick={() => increment(setChildren, children)}
                  disabled={children >= 4}
                >
                  +
                </button>
              </div>
            </div>

            <div className="traveler-row">
              <span className="traveler-label">
                {intl.formatMessage({ id: "rooms" })}
              </span>
              <div className="counter">
                <button
                  className="counter-btn"
                  onClick={() => decrement(setRooms, rooms)}
                  disabled={rooms <= 1}
                >
                  -
                </button>
                <span className="counter-value">{rooms}</span>
                <button
                  className="counter-btn"
                  onClick={() => increment(setRooms, rooms)}
                  disabled={rooms >= adults + children}
                >
                  +
                </button>
              </div>
            </div>

            {Array.isArray(childrenAges) && childrenAges.length > 0 && (
              <div className="children-ages-section">
                <div className="section-title">
                  {intl.formatMessage({ id: "childrenAges" })}
                </div>
                {childrenAges.map((age, idx) => (
                  <div key={idx} className="age-select-wrapper">
                    <label className="age-label">
                      {intl.formatMessage({ id: "child" })} {idx + 1}
                    </label>
                    <select
                      className="age-select"
                      value={age}
                      onChange={(e) => {
                        let ages = [...childrenAges];
                        ages[idx] = e.target.value * 1;
                        setChildrenAges(ages);
                      }}
                    >
                      <option value={0} disabled>
                        {intl.formatMessage({ id: "pleaseSelect" })}
                      </option>
                      {[
                        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
                        17,
                      ].map((ageOption, idxAge) => (
                        <option key={idxAge} value={ageOption}>
                          {ageOption === 1 ? "<2" : ageOption}{" "}
                          {intl.formatMessage({ id: "years" })}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {multiRooms.length > 0 && (
              <div className="multi-rooms-section">
                <div className="section-title">
                  {intl.formatMessage({ id: "roomDistribution" })}
                </div>
                {multiRooms.map((roomCount) => {
                  let adultsCount = adults * 1;
                  let childrenCount = children * 1;
                  const adultsPerRoom = adultsCount / roomCount;
                  const childrenPerRoom = childrenCount / roomCount;

                  return (
                    <div key={roomCount} className="room-option">
                      <label className="room-option-label">
                        <input
                          type="radio"
                          className="room-radio"
                          checked={rooms === roomCount}
                          name="multiroomcheck"
                          id={`multiroomradio${roomCount}`}
                          onChange={() => setRooms(roomCount)}
                        />
                        <div className="room-details">
                          <span className="room-count">
                            {roomCount}{" "}
                            {roomCount > 1
                              ? intl.formatMessage({ id: "roomsWithEach" })
                              : intl.formatMessage({ id: "roomWith" })}
                          </span>
                          <span className="guest-distribution">
                            {adultsPerRoom}{" "}
                            {adultsPerRoom > 1
                              ? intl.formatMessage({ id: "adultsPlural" })
                              : intl.formatMessage({ id: "adultSingular" })}
                            {childrenCount > 0 && (
                              <>
                                {" "}
                                {intl.formatMessage({ id: "and" })}{" "}
                                {childrenPerRoom}
                                {childrenPerRoom > 1
                                  ? " " +
                                    intl.formatMessage({ id: "childrenPlural" })
                                  : " " +
                                    intl.formatMessage({ id: "childSingular" })}
                              </>
                            )}
                          </span>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="popup-footer">
          {!childrenAges.includes(0) && (
            <Button
              text={intl.formatMessage({ id: "apply" })}
              onClick={handleApply}
              size={"m"}
              color={"orange"}
              style={"rounded"}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TravelersPopUp;
